import { AxiosResponse } from "axios";
import axiosClient from "./axios-client"; // Import the axiosClient instance
import {
  CreditEntryResponse,
  CreditHistoryDetail,
  CreditHistoryItem,
  CreditHistoryParams,
  CreditPlan,
  CreditPurchaseRequest,
  TokenSummaryResponse,
} from "@pages/Credits/types";

// Credit API Endpoints
const CREDIT_BALANCE_URL = "/Credit/UserCreditBalance";
const GET_ACTIVE_REQUEST_COST_URL = "/Credit/GetActiveRequestCost";
const GET_TOKEN_SUMMARY_URL = "/Credit/GetTokenSummary";
const GET_CREDIT_HISTORY_URL = "/Credit/GetCreditHistory";
const GET_CREDIT_HISTORY_DETAILS_URL = "/Credit/GetCreditHistoryDetails/?id=";
const GET_CREDIT_PLANS_URL = "/Credit/GetCreditPlans";
const CREDIT_PURCHASE_URL = "/Credit/CreditPurchase";
const AUTO_PURCHASE_URL = "/Credit/AutoPurchase";
const DELETE_CARD_URL = "/Credit/DeleteCard";

/**
 * Fetch the user's token summary.
 * @returns The user's token summary counts (total, earned, spent).
 */
export const fetchTokenSummary = async (): Promise<TokenSummaryResponse> => {
  try {
    const data: TokenSummaryResponse = await axiosClient.get(GET_TOKEN_SUMMARY_URL);

    return {
      totalTokens: typeof data?.totalTokens === "number" ? data.totalTokens : 0,
      tokensEarned: typeof data?.tokensEarned === "number" ? data.tokensEarned : 0,
      tokensSpent: typeof data?.tokensSpent === "number" ? data.tokensSpent : 0,
    };
  } catch (error) {
    console.error("Error fetching token summary:", error);
    return {
      totalTokens: 0,
      tokensEarned: 0,
      tokensSpent: 0,
    };
  }
};


/**
 * Fetch the user's credit balance.
 * @returns The user's credit balance data.
 */

export interface CreditBalanceResponse {
  availableCredits: number;
  isAutoPurchase: boolean;
}

export const fetchUserCreditBalance = async (): Promise<CreditBalanceResponse> => {
  try {
    // Fetch the response which is directly the CreditBalanceResponse
    const data: CreditBalanceResponse = await axiosClient.get(CREDIT_BALANCE_URL);

    // Validate response data
    if (typeof data.availableCredits !== "number" || typeof data.isAutoPurchase !== "boolean") {
      throw new Error(`Invalid response: `);
    }

    return data;
  } catch (error) {
    console.error("Error fetching credit balance:", error);
    throw error;
  }
};

/**
 * Fetch how many tokens are deducted for posting a load request, from the
 * currently active credit configuration.
 */
export const fetchActiveRequestCost = async (): Promise<number> => {
  try {
    const data: { requestCost: number } = await axiosClient.get(GET_ACTIVE_REQUEST_COST_URL);
    return typeof data?.requestCost === "number" ? data.requestCost : 0;
  } catch (error) {
    console.error("Error fetching active request cost:", error);
    return 0;
  }
};

export interface AutoPurchaseResponse {
  message: string;
  newUserCreditBalance: number;
}

export const fetchAutoPurchase = async (): Promise<AutoPurchaseResponse | null> => {
  try {
    const data: AutoPurchaseResponse = await axiosClient.post(AUTO_PURCHASE_URL);
    return typeof data?.newUserCreditBalance === "number" ? data : null;
  } catch (error) {
    // Auto-purchase is a side effect of opening the page; it must not block the rest of the data.
    console.error("Error fetching auto purchase:", error);
    return null;
  }
};
/**
 * Fetch the user's credit history.
 * @param params Pagination and sorting parameters for credit history.
 * @returns The user's credit history data.
 */
export const getUserCreditHistory = async (params: CreditHistoryParams): Promise<CreditEntryResponse> => {
  try {
    // Since your axios client returns the array directly
    const creditHistory: CreditEntryResponse = await axiosClient.get(GET_CREDIT_HISTORY_URL, { params });

    return creditHistory;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching credit history:", error.message);
    } else {
      console.error("Unknown error fetching credit history");
    }
    throw error;
  }
};

export const getCreditHistoryDetails = async (id: number): Promise<CreditHistoryDetail> => {
  try {
    const response: AxiosResponse<CreditHistoryDetail> = await axiosClient.get(`${GET_CREDIT_HISTORY_DETAILS_URL}${id}`);
    // Return the whole response if data is nested or not inside response.data
    return response as unknown as CreditHistoryDetail;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching credit history details:", error.message);
    } else {
      console.error("Unknown error fetching credit history details");
    }
    throw error;
  }
};

export const getCreditPlans = async (): Promise<CreditPlan[]> => {
  try {
    const response: CreditPlan[] = await axiosClient.get(GET_CREDIT_PLANS_URL);
    return response; // No .data, return the response directly
  } catch (error) {
    console.error("Error fetching credit plans:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

export const creditPurchase = async (purchaseData: CreditPurchaseRequest): Promise<any> => {
  try {
    const response = await axiosClient.post(CREDIT_PURCHASE_URL, purchaseData);
    return response;
  } catch (error) {
    console.error("Error purchasing credits:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

// Define the CreditCard interface for type safety
export interface CreditCard {
  paymentMethodId: string;
  cardType: string;
  cardLast4: string;
  expirationMonth: number;
  expirationYear: number;
}

export const getCreditCards = async (): Promise<CreditCard[]> => {
  try {
    const cards: CreditCard[] = await axiosClient.get("/Credit/GetCards");
    return cards;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching credit cards:", error.message);
    } else {
      console.error("Unknown error fetching credit cards");
    }
    throw error;
  }
};

export interface SaveCardRequest {
  paymentMethodId: string;
}

export const saveCard = async (cardData: SaveCardRequest): Promise<any> => {
  try {
    const response = await axiosClient.post("/Credit/SaveCard", cardData);
    return response; // Returning the response directly
  } catch (error) {
    console.error("Error saving card:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

export interface PurchaseEnablesResponse {
  isAutoPurchaseEnabled: boolean;
  autoPurchaseCreditPlanId: number;
  autoPurchaseCreditLimit: number;
  isMonthlyPurchaseEnabled: boolean;
  monthlyCreditPlanId: number;
}

export interface UpdatePurchaseEnable {
  isAutoPurchaseEnabled: boolean;
  autoPurchaseCreditPlanId: number;
  autoPurchaseCreditLimit: number;
  isMonthlyPurchaseEnabled: boolean;
  monthlyPurchaseCreditPlanId: number;
  currency: string;
}

export const getPurchaseEnables = async (): Promise<PurchaseEnablesResponse> => {
  try {
    const response = await axiosClient.get("/Credit/PurchaseEnables");
    return response as unknown as PurchaseEnablesResponse; // Return the response data
  } catch (error) {
    console.error("Error fetching purchase enables:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

export const updatePurchaseSettings = async (purchaseData: UpdatePurchaseEnable): Promise<UpdatePurchaseEnable> => {
  try {
    const response = await axiosClient.post("/Credit/UpdatePurchaseSettings", purchaseData);
    return response as unknown as UpdatePurchaseEnable; // Return the response data
  } catch (error) {
    console.error("Error fetching purchase enables:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

export const deleteCard = async (paymentMethodId: SaveCardRequest): Promise<any> => {
  try {
    await axiosClient.post("/Credit/DeleteCard", paymentMethodId);
  } catch (error) {
    console.error("Error fetching purchase enables:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};
